import React, { useMemo } from 'react';
import { Empty, Tooltip } from 'antd';
import { useTheme } from '../../context/ThemeContext';

const GUN_MS = 86400000;

export const GUN = GUN_MS;
export const gunFarki = (a, b) => Math.round((a.getTime() - b.getTime()) / GUN_MS);
export const gunEkle = (d, n) => new Date(d.getTime() + n * GUN_MS);

/** Geçersiz / 1970 / 0000-00-00 kaynaklı tarihleri eler. */
export const tariheCevir = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime()) || d.getFullYear() < 1990) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const kisaTarih = (d) => d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });

export const GANTT_RENK = {
  tamamlandi: '#52c41a',
  aktif: '#1677ff',
  bekliyor: '#d9d9d9',
  beklemede: '#faad14',
  asim: '#f5222d',
  // Erken biten adımda plandan artan süre (kazanç)
  erken: '#13c2c2',
};

/**
 * Genel amaçlı Gantt çizeri — yeni npm paketi kullanmaz, saf SVG.
 *
 * Beklenen veri:
 *   satirlar: [{ anahtar, etiket, cubuklar: [{ ad, durum, baslangic: Date,
 *                planBitis: Date, gercekBitis: Date, asimGun, terminGun,
 *                altDurum }] }]
 *
 * Bir çubuğun planBitis'ini aşan kısmı kırmızı çizilir; çubuklar zaten
 * gerçek bitişe göre zincirlendiği için sonraki adımlar kendiliğinden kayar.
 */
const GanttChart = ({ satirlar = [], satirYukseklik = 26, bosMesaj = 'Çizilecek veri yok' }) => {
  const { darkMode } = useTheme();

  // Ölçek penceresi: bozuk tek bir tarih (ör. kurulum 2055) tüm grafiği
  // on binlerce güne yayıp tarayıcıyı kilitliyordu. Pencere bugüne göre
  // sınırlanır; dışarıda kalan çubuklar kırpılır.
  const GERI_GUN = 3 * 365;
  const ILERI_GUN = 400;
  const MAKS_SATIR = 150;

  const gorunen = useMemo(() => satirlar.slice(0, MAKS_SATIR), [satirlar]);
  const kirpilanSatir = satirlar.length - gorunen.length;

  const olcek = useMemo(() => {
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);
    const altSinir = gunEkle(bugun, -GERI_GUN);
    const ustSinir = gunEkle(bugun, ILERI_GUN);

    let enErken = null;
    let enGec = null;
    gorunen.forEach((s) => {
      s.cubuklar.forEach((c) => {
        if (c.baslangic < altSinir || c.baslangic > ustSinir) return;
        if (!enErken || c.baslangic < enErken) enErken = c.baslangic;
        const bitis = c.gercekBitis > c.planBitis ? c.gercekBitis : c.planBitis;
        const sinirli = bitis > ustSinir ? ustSinir : bitis;
        if (!enGec || sinirli > enGec) enGec = sinirli;
      });
    });
    if (!enErken) return null;
    if (bugun > enGec) enGec = bugun;
    if (bugun < enErken) enErken = bugun;
    const toplamGun = Math.max(1, Math.min(GERI_GUN + ILERI_GUN, gunFarki(enGec, enErken) + 2));
    return { baslangic: enErken, toplamGun, bugun, bitis: gunEkle(enErken, toplamGun) };
  }, [gorunen]);

  if (!satirlar.length || !olcek) return <Empty description={bosMesaj} />;

  const { baslangic, toplamGun, bugun, bitis: pencereBitis } = olcek;
  const gunGenislik = Math.max(3, Math.min(14, Math.round(1400 / toplamGun)));
  const grafikGenislik = toplamGun * gunGenislik;
  const etiketGenislik = 210;
  const basliksatir = 30;
  const toplamYukseklik = basliksatir + gorunen.length * satirYukseklik + 8;

  const metinRenk = darkMode ? '#d9d9d9' : '#333';
  const cizgiRenk = darkMode ? '#434343' : '#eee';

  // Yer kısıtlı olduğu için yalnızca ay başlarına etiket koyulur.
  const eksen = [];
  for (let g = 0; g < toplamGun; g += 1) {
    const d = gunEkle(baslangic, g);
    if (d.getDate() === 1 || g === 0) eksen.push({ g, d });
  }
  const bugunOfset = gunFarki(bugun, baslangic);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={etiketGenislik + grafikGenislik + 16} height={toplamYukseklik} style={{ display: 'block' }}>
        {eksen.map(({ g, d }) => (
          <g key={g}>
            <line
              x1={etiketGenislik + g * gunGenislik} y1={basliksatir - 8}
              x2={etiketGenislik + g * gunGenislik} y2={toplamYukseklik}
              stroke={cizgiRenk}
            />
            <text x={etiketGenislik + g * gunGenislik + 2} y={basliksatir - 12} fontSize="10" fill={metinRenk}>
              {kisaTarih(d)}
            </text>
          </g>
        ))}

        {bugunOfset >= 0 && bugunOfset <= toplamGun && (
          <line
            x1={etiketGenislik + bugunOfset * gunGenislik} y1={basliksatir - 8}
            x2={etiketGenislik + bugunOfset * gunGenislik} y2={toplamYukseklik}
            stroke="#ff4d4f" strokeDasharray="3 3"
          />
        )}

        {gorunen.map((satir, i) => {
          const y = basliksatir + i * satirYukseklik;
          return (
            <g key={satir.anahtar}>
              <text x={4} y={y + satirYukseklik / 2 + 4} fontSize="11" fill={metinRenk}>
                {satir.etiket.length > 30 ? `${satir.etiket.slice(0, 29)}…` : satir.etiket}
              </text>
              {satir.cubuklar.map((c, ci) => {
                // Pencere dışındaki çubuk çizilmez (bozuk tarihler).
                if (c.baslangic < baslangic || c.baslangic > pencereBitis) return null;
                const x = etiketGenislik + gunFarki(c.baslangic, baslangic) * gunGenislik;
                const planGen = Math.max(2, gunFarki(c.planBitis, c.baslangic) * gunGenislik);
                // Çubuk artık GERÇEK süreyi çizer. Önceden her zaman plan boyu
                // çiziliyordu: erken biten adım tam termin boyunda görünüyor ve
                // sonraki adımın çubuğuyla örtüşüyordu.
                const gercekGen = Math.max(2, gunFarki(c.gercekBitis, c.baslangic) * gunGenislik);
                const asimGen = c.asimGun > 0 ? c.asimGun * gunGenislik : 0;
                const kazancGen = c.kazancGun > 0 ? c.kazancGun * gunGenislik : 0;
                const ipucu =
                  `${c.ad}${c.altDurum ? ` — ${c.altDurum}` : ''}\n` +
                  `Termin: ${c.terminGun} gün\n` +
                  `${kisaTarih(c.baslangic)} → ${kisaTarih(c.gercekBitis)}` +
                  (c.olculemedi ? '\n(gerçek bitiş kaydı yok — termin barı)' : '') +
                  (c.asimGun > 0 ? `\nAŞIM: ${c.asimGun} gün` : '') +
                  (c.kazancGun > 0 ? `\nERKEN BİTTİ: ${c.kazancGun} gün` : '');
                return (
                  <Tooltip
                    key={`${satir.anahtar}-${c.ad}-${ci}`}
                    title={<span style={{ whiteSpace: 'pre-line' }}>{ipucu}</span>}
                  >
                    <g>
                      {/* Plan hattı — planlanan süre her zaman referans olarak
                          ince bir çizgiyle görünür; gerçek çubuk bunun üstünde. */}
                      <rect
                        x={x} y={y + 2} width={planGen} height={3} rx={1.5}
                        fill="#8c8c8c" opacity={0.35}
                      />
                      <rect
                        x={x} y={y + 6} width={gercekGen} height={satirYukseklik - 12} rx={3}
                        fill={GANTT_RENK[c.durum] || GANTT_RENK.bekliyor}
                        opacity={c.durum === 'bekliyor' ? 0.45 : 0.9}
                        stroke={c.olculemedi ? '#8c8c8c' : 'none'}
                        strokeDasharray={c.olculemedi ? '3 2' : undefined}
                      />
                      {/* Erken bitiş kazancı: gerçek bitişten plan bitişine kadar */}
                      {kazancGen > 0 && (
                        <rect
                          x={x + gercekGen} y={y + 6} width={kazancGen}
                          height={satirYukseklik - 12} rx={3}
                          fill={GANTT_RENK.erken} opacity={0.28}
                          stroke={GANTT_RENK.erken} strokeDasharray="3 2"
                        />
                      )}
                      {asimGen > 0 && (
                        <rect
                          x={x + planGen} y={y + 6} width={asimGen} height={satirYukseklik - 12}
                          rx={3} fill={GANTT_RENK.asim} opacity={0.9}
                        />
                      )}
                    </g>
                  </Tooltip>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        {[
          ['tamamlandi', 'Tamamlandı'],
          ['aktif', 'Devam eden'],
          ['bekliyor', 'Bekleyen'],
          ['beklemede', 'Beklemede'],
          ['asim', 'Termin aşımı'],
          ['erken', 'Erken bitti (kazanç)'],
        ].map(([k, label]) => (
          <span key={k}>
            <span style={{
              display: 'inline-block', width: 14, height: 10, borderRadius: 2,
              background: GANTT_RENK[k], marginRight: 4, verticalAlign: 'middle',
            }} />
            {label}
          </span>
        ))}
        <span style={{ color: '#8c8c8c' }}>
          Kesikli kırmızı çizgi bugünü gösterir. Termini aşan adımlar sonrakileri sağa kaydırır.
          Kesikli çerçeveli çubuklar ölçülemedi (gerçek bitiş kaydı yok, termin barı çizildi).
        </span>
        {kirpilanSatir > 0 && (
          <span style={{ color: '#fa8c16' }}>
            Performans için ilk {MAKS_SATIR} satır çizildi; {kirpilanSatir} satır gösterilmiyor
            (listeyi filtreleyin).
          </span>
        )}
      </div>
    </div>
  );
};

export default GanttChart;
